import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/toast/ToastProvider';
import styles from '@/pages/IntegrationsPage.module.css';

type CampaignRow = {
  brevo_campaign_id: number;
  name: string;
  subject?: string | null;
  brevo_status: string | null;
  stats: Record<string, unknown>;
  sent_at: string | null;
  last_stats_at?: string | null;
};

type Props = {
  integrationId: string;
  enabled: boolean;
};

export function BrevoCampaignsPanel({ integrationId, enabled }: Props) {
  const qc = useQueryClient();
  const toast = useToast();

  const campaignsQ = useQuery({
    queryKey: ['brevo-campaigns', integrationId],
    enabled: enabled && Boolean(integrationId),
    queryFn: () =>
      apiFetch<{
        hub_campaigns: CampaignRow[];
        imported_campaigns: CampaignRow[];
        automation_event_name: string;
      }>(`/api/integrations/${integrationId}/brevo/campaigns`),
  });

  const syncCampaigns = useMutation({
    mutationFn: () =>
      apiFetch<{ imported: number; updated: number }>(
        `/api/integrations/${integrationId}/brevo/campaigns/sync`,
        { method: 'POST', body: '{}' },
      ),
    onSuccess: (data) => {
      toast.push(
        `Brevo-campagnes gesynchroniseerd (${data.imported} nieuw, ${data.updated} bijgewerkt)`,
        'success',
      );
      void qc.invalidateQueries({ queryKey: ['brevo-campaigns', integrationId] });
    },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });

  if (!enabled) return null;

  const eventName = campaignsQ.data?.automation_event_name ?? 'mh_lead_status_changed';
  const hub = campaignsQ.data?.hub_campaigns ?? [];
  const imported = campaignsQ.data?.imported_campaigns ?? [];

  return (
    <div className={styles.brevoAdvanced}>
      <h4>Brevo campagnes &amp; automations</h4>
      <p className={styles.mutedSmall}>
        Importeer bestaande Brevo e-mailcampagnes (alleen lezen). Hub-broadcasts verschijnen onder
        &quot;Vanuit Hub&quot;.
      </p>
      <p className={styles.mutedSmall}>
        <strong>Automation-trigger:</strong> zet in Brevo een workflow op entry{' '}
        <em>Custom event</em> <code>{eventName}</code> (properties:{' '}
        <code>from_status</code>, <code>to_status</code>, <code>mh_lead_id</code>). Flows kun je
        niet vanuit Hub aanmaken — alleen triggers sturen.
      </p>
      <Button
        variant="secondary"
        disabled={syncCampaigns.isPending}
        onClick={() => syncCampaigns.mutate()}
      >
        {syncCampaigns.isPending ? 'Synchroniseren…' : 'Campagnes ophalen uit Brevo'}
      </Button>

      {campaignsQ.isLoading ? <p className={styles.mutedSmall}>Laden…</p> : null}

      {hub.length > 0 ? (
        <>
          <p className={styles.sectionLabel}>Vanuit Hub</p>
          <ul className={styles.campaignList}>
            {hub.map((c) => (
              <li key={c.brevo_campaign_id}>
                <strong>{c.name}</strong> · #{c.brevo_campaign_id}
                {c.brevo_status ? ` · ${c.brevo_status}` : ''}
                {c.sent_at ? ` · ${new Date(c.sent_at).toLocaleDateString('nl-NL')}` : ''}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {imported.length > 0 ? (
        <>
          <p className={styles.sectionLabel}>Alleen in Brevo</p>
          <ul className={styles.campaignList}>
            {imported.map((c) => (
              <li key={c.brevo_campaign_id}>
                <strong>{c.name}</strong>
                {c.subject ? ` — ${c.subject}` : ''} · #{c.brevo_campaign_id}
                {c.brevo_status ? ` · ${c.brevo_status}` : ''}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {!campaignsQ.isLoading && hub.length === 0 && imported.length === 0 ? (
        <p className={styles.mutedSmall}>Nog geen campagnes. Klik ophalen na je eerste sends in Brevo.</p>
      ) : null}
    </div>
  );
}
